import { ErrorService } from '@/shared/service/errorService'
import { normalizeStringForSearch } from '@/shared/service/utils'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

import { t } from '../../i18n/i18n'
import router from '../../router'
import { SnackbarService } from '../../shared/snackbar/snackbar.service'
import { type NewUser, type User } from './users.service'
import { UsersService } from './users.service'

class UsersState {
  users: Record<string, User> = {}
  loading = false
  search = ''
  deletingUserId: string | null = null
  editingUser: User | null = null
}

type UsersStore = UsersState & {
  setUsers: (users: User[]) => void
  load: () => void
  unload: () => void
  setSearch: (val: string) => void
  reset: () => void
  setDeleting: (id: string) => void
  setNotDeleting: () => void
  setEditing: (user: User) => void
  list: () => Promise<void>
  deleteUser: () => Promise<void>
  resetPassword: (id: string) => Promise<void>
  blockUser: (id: string) => Promise<void>
  addUser: (user: NewUser) => Promise<void>
  loadUser: (id: string) => Promise<void>
}

export const useUsersStore = create<UsersStore>((set, get) => ({
  ...new UsersState(),
  setUsers: (users) => {
    set({
      users: users.reduce<Record<string, User>>((acc, user) => {
        acc[user._id] = user
        return acc
      }, {}),
    })
  },
  load: () => {
    set({ loading: true })
  },
  unload: () => {
    set({ loading: false })
  },
  setSearch: (val) => {
    set({ search: val })
  },
  reset: () => {
    set({
      search: '',
      deletingUserId: null,
      editingUser: null,
      loading: false,
    })
  },
  setDeleting: (id) => {
    set({ deletingUserId: id })
  },
  setNotDeleting: () => {
    set({ deletingUserId: null })
  },
  setEditing: (user) => {
    set({ editingUser: cloneDeep(user) })
  },
  async list() {
    try {
      set({ loading: true })
      const users = await UsersService.getAll()
      get().setUsers(users)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
  async deleteUser() {
    try {
      set({ loading: true })
      const { deletingUserId, users } = get()
      if (deletingUserId == null || deletingUserId === '') return
      await UsersService.deleteUser(deletingUserId)
      SnackbarService.info(
        t('USERS.SNACKBAR.DELETE', {
          string: users[deletingUserId].name,
        }),
      )
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().reset()
    }
  },
  async resetPassword(id: string) {
    try {
      set({ loading: true })
      await UsersService.resetPassword(id)
      SnackbarService.info(
        t('USERS.SNACKBAR.RESET', {
          string: get().users[id].name,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
  async blockUser(id: string) {
    try {
      set({ loading: true })
      const user = cloneDeep(get().users[id])
      user.blocked = !(user.blocked ?? false)
      await UsersService.updateUser(user)
      const message = user.blocked
        ? t('USERS.SNACKBAR.BLOCK', { string: user.name }).toString()
        : t('USERS.SNACKBAR.UNBLOCK', { string: user.name }).toString()
      SnackbarService.info(message)
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
  async addUser(user: NewUser) {
    try {
      let message = ''
      const { editingUser } = get()
      if (editingUser) {
        await UsersService.updateUser({ ...user, _id: editingUser._id })
        message = t('USERS.SNACKBAR.EDIT', { string: user.name }).toString()
      } else {
        // Never POST an `_id` (an empty one is saved as is by the backend and
        // the next creation would overwrite that account)
        const newUser = { ...user }
        delete newUser._id
        await UsersService.addUser(newUser)
        message = t('USERS.SNACKBAR.ADD', { string: user.name }).toString()
      }

      SnackbarService.info(message)
      get().reset()
      await router.push('/users')
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
  async loadUser(id: string) {
    try {
      set({ loading: true })
      const user = await UsersService.getUserById(id)
      if (user != null) get().setEditing(user)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      set({ loading: false })
    }
  },
}))

function computeFilteredUsers(state: UsersState): User[] {
  return state.search && state.search.length > 0
    ? Object.values(state.users).filter(
        (u) =>
          normalizeStringForSearch(u.name).includes(
            normalizeStringForSearch(state.search),
          ) ||
          normalizeStringForSearch(u.email).includes(
            normalizeStringForSearch(state.search),
          ),
      )
    : Object.values(state.users)
}

function computeUser(state: UsersState): NewUser {
  return state.editingUser
    ? state.editingUser
    : {
        name: '',
        email: '',
        roles: [],
        expiryDate: '',
        blocked: false,
      }
}

export { computeFilteredUsers, computeUser }
